import { datadogRum } from '@datadog/browser-rum';
import { type ReactNode, useContext, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as Sentry from '@sentry/nextjs';

import { CustomerCartContext, CustomerCompareListContext, CustomerContext, ProjectContext } from '@tmh-website/shared';
import { getCartData, getCustomerData } from '@tmh-website/services/magento/cart-customer.service';
import { getCompareListData } from '@tmh-website/services/magento/compare-list.service';
import { getCustomerDataModule } from '@tmh-website/services/magento/customer-data.service';
import { type LocalePrefix, hasShop } from '@tmh-website/shared/const';

const localeStorageCompareProductChangeDetectionKey = 'TMH_compare-product-change-detection';

/**
 * Loads customer and cart details from Magento and makes them available through contexts.
 */
type CustomerContainerProps = { children: ReactNode; locale: LocalePrefix };
const CustomerContainer = ({ children, locale }: CustomerContainerProps) => {
  const useStubData = process.env.NODE_ENV === 'development';
  const { project } = useContext(ProjectContext);
  const envHasShop = hasShop(locale, project);

  const {
    data: customer,
    isLoading: customerIsLoading,
    refetch: refetchCustomer,
  } = useQuery(['customer', locale], () => getCustomerData({ locale, useStubData }), { enabled: envHasShop });

  const {
    data: cart,
    isLoading: cartIsLoading,
    refetch: refetchCartData,
  } = useQuery(
    ['cart', customer?.cartId, locale],
    () => {
      if (!customer?.cartId) {
        return null;
      }
      return getCartData({ cartId: customer!.cartId, locale, useStubData });
    },
    {
      enabled: envHasShop,
    }
  );

  const {
    data: compareList,
    isLoading: compareListIsLoading,
    refetch: refetchCompareList,
  } = useQuery(
    ['compare-list'],
    () => {
      if (!customer) {
        return null;
      }
      return getCompareListData(locale);
    },
    {
      enabled: envHasShop,
    }
  );

  const refetchCart = async () => {
    if (!customer) {
      return;
    }
    if (!customer?.cartId) {
      // No cart id is present, we might not have the latest cart id. Refetch customer data and the cart will automatically be updated in case the cart id has changed.
      await refetchCustomer();
    } else {
      await refetchCartData();
    }
  };

  useEffect(() => {
    if (!envHasShop) {
      return;
    }
    (async () => {
      try {
        const customerData = await getCustomerDataModule(locale);

        // Listen to changes and reload respective data
        customerData.get('cart').subscribe(async () => {
          // Important: We cannot call `refetchCart` here, because `customer` is not in the same scope when this callback is called.
          // TODO @florianmrz can this ^ be fixed?
          await refetchCustomer();
          await refetchCartData();
        });
        customerData.get('compare-products').subscribe(event => {
          /**
           * When adding a new product to the compare list via the product detail page, this triggers the "compare-products" event.
           * This leads to an infinite loop of refetching the compare list.
           * Since we cannot access the React context inside of this callback function, we store a change detection key in the local storage and compare against it.
           * If the key is the same, we do not refetch the compare list and thus break the loop.
           */
          const newChangeDetectionKey = event.items
            .map(item => window.btoa(item.id))
            .sort()
            .join(',');

          const currentChangeDetectionKey = window.localStorage.getItem(localeStorageCompareProductChangeDetectionKey);

          if (newChangeDetectionKey === currentChangeDetectionKey) {
            return;
          }

          window.localStorage.setItem(localeStorageCompareProductChangeDetectionKey, newChangeDetectionKey);

          refetchCompareList();
        });
      } catch (error) {
        Sentry.captureException(error);
        console.error('Failed to load customer-data require module.');
        console.error(error);
      }
    })();
  }, []);

  useEffect(() => {
    if (!customer?.customer) {
      datadogRum.removeUser();
      Sentry.setUser(null);
      return;
    }
    datadogRum.setUser({
      email: customer.customer.email,
      name: `${customer.customer.firstName} ${customer.customer.lastName}`,
    });
    Sentry.setUser({
      email: customer.customer.email,
      username: `${customer.customer.firstName} ${customer.customer.lastName}`,
    });
  }, [customer]);

  return (
    <CustomerContext.Provider
      value={{
        customer: customer?.customer,
        wishlist: customer?.wishlist,
        isLoading: customerIsLoading,
        refetch: refetchCustomer,
      }}>
      <CustomerCartContext.Provider
        value={{
          cart,
          isLoading: cartIsLoading,
          refetch: refetchCart,
        }}>
        <CustomerCompareListContext.Provider
          value={{
            compareList,
            isLoading: compareListIsLoading,
            refetch: refetchCompareList,
          }}>
          {children}
        </CustomerCompareListContext.Provider>
      </CustomerCartContext.Provider>
    </CustomerContext.Provider>
  );
};

export default CustomerContainer;
